import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';
import { 
  DollarSign, 
  TrendingUp, 
  Leaf, 
  Clock, 
  Layers, 
  Sparkles, 
  ChevronRight, 
  ChevronUp,
  ChevronDown,
  X, 
  Download, 
  Zap, 
  Sliders, 
  CheckCircle2, 
  AlertCircle, 
  PieChart, 
  Info, 
  BarChart3,
  FileSpreadsheet,
  Scissors,
  Bot,
  RefreshCw,
  FileDown,
  Loader2
} from 'lucide-react';
import { VdpLayout, VdpTemplate, CSVData, Unit } from '../../types';
import { useSaaSContext } from '../../store/SaaSContext';
import { useToast } from '../../store/ToastContext';
import { formatUnit } from '../../utils/units';
import { useRealtimeCurrency } from '../../hooks/useRealtimeCurrency';
import { 
  SubstratePreset, 
  getAvailableSubstrates, 
  DEFAULT_SUBSTRATE_PRESETS 
} from '../../utils/substratePresets';
import { SubstrateCustomizerModal } from './SubstrateCustomizerModal';
import { AICalculatorModal } from './AICalculatorModal';

export type { SubstratePreset };
export { DEFAULT_SUBSTRATE_PRESETS as SUBSTRATE_PRESETS };

interface RoiCalculatorPanelProps {
  layout?: VdpLayout;
  template?: VdpTemplate;
  csvData?: CSVData | null;
  appUnit?: Unit;
  finalSheetWidth?: number;
  finalSheetHeight?: number;
  cols?: number;
  rows?: number;
  compact?: boolean;
  
  onUpdateLayout?: (key: keyof VdpLayout, value: any) => void;
  onAutoCalculate?: () => void;
  onCenterOnPage?: () => void;
  
}

export const CURRENCY_OPTIONS = [
  { symbol: '₹', code: 'INR', label: '₹ INR (Indian Rupee)' },
  { symbol: '$', code: 'USD', label: '$ USD (US Dollar)' },
  { symbol: '€', code: 'EUR', label: '€ EUR (Euro)' },
  { symbol: '£', code: 'GBP', label: '£ GBP (British Pound)' },
  { symbol: 'AED', code: 'AED', label: 'AED (UAE Dirham)' },
  { symbol: 'C$', code: 'CAD', label: 'C$ CAD (Canadian Dollar)' },
  { symbol: 'A$', code: 'AUD', label: 'A$ AUD (Australian Dollar)' },
  { symbol: 'S$', code: 'SGD', label: 'S$ SGD (Singapore Dollar)' },
  { symbol: 'SAR', code: 'SAR', label: 'SAR (Saudi Riyal)' },
  { symbol: '¥', code: 'JPY', label: '¥ JPY (Japanese Yen)' }
];

export const RoiCalculatorPanel: React.FC<RoiCalculatorPanelProps> = (props) => {
  const saas = useSaaSContext();
  const layout = props.layout ?? saas.layout;
  const template = props.template ?? saas.template;
  const csvData = props.csvData ?? saas.csvData;
  const appUnit = props.appUnit ?? saas.appUnit;
  const onUpdateLayout = props.onUpdateLayout ?? ((key, value) => saas.setLayout(prev => ({ ...prev, [key]: value })));
  const onAutoCalculate = props.onAutoCalculate ?? (() => {});
  const onCenterOnPage = props.onCenterOnPage ?? (() => {});

  // Geometry calculations
  const rawSheetWidth = layout.pageSize === 'custom' && layout.pageWidth ? Number(layout.pageWidth) : (layout.pageSize === 'A3' ? 842 : 595);
  const rawSheetHeight = layout.pageSize === 'custom' && layout.pageHeight ? Number(layout.pageHeight) : (layout.pageSize === 'A3' ? 1190 : 842);
  const isLandscape = layout.pageOrientation === 'landscape';
  const finalSheetWidth = props.finalSheetWidth ?? (isLandscape ? rawSheetHeight : rawSheetWidth);
  const finalSheetHeight = props.finalSheetHeight ?? (isLandscape ? rawSheetWidth : rawSheetHeight);
  const cols = props.cols ?? (Number(layout.cols) || 1);
  const rows = props.rows ?? (Number(layout.rows) || 1);

  const { toast } = useToast();
  const { activeCode, supportedCurrencies, switchCurrency, refreshRates, isRefreshing, exchangeRateString } = useRealtimeCurrency();
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showSubstrateModal, setShowSubstrateModal] = useState(false);
  const [auditTab, setAuditTab] = useState<'economics' | 'optimizer' | 'esg' | 'quote'>('economics');
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Close Audit Modal on Escape
  useEffect(() => {
    if (!showAuditModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowAuditModal(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAuditModal]);
  
  // Available substrate presets (dynamic including user custom)
  const availableSubstrates = useMemo(() => getAvailableSubstrates(), [showSubstrateModal, layout.substrateType]);

  // What-If Simulation interactive overrides
  const [simQuantity, setSimQuantity] = useState<number>(0); // 0 means use dataset default
  const [targetMarginPercent, setTargetMarginPercent] = useState<number>(50); // For quote selling price calculation

  // Currency & Unit Cost state
  const currSym = layout.currencySymbol || '₹';
  const unitCost = layout.costPerSheet !== undefined ? layout.costPerSheet : 12;
  const inkCost = layout.inkCostPerSheet !== undefined ? layout.inkCostPerSheet : 1.5;
  const laborRate = layout.laborRatePerHour !== undefined ? layout.laborRatePerHour : 300;
  const pressSpeed = layout.pressSpeedPpm !== undefined ? layout.pressSpeedPpm : 20; // 20 ppm default

  // Base production geometry calculations
  const disabledSlotCount = layout.disabledSlots?.length || 0;
  const rawGridCount = cols * rows;
  const activeGridCount = Math.max(0, rawGridCount - disabledSlotCount);
  
  const sheetArea = finalSheetWidth * finalSheetHeight;
  const singleStickerArea = Number(template.width) * Number(template.height);
  const totalPrintedArea = activeGridCount * singleStickerArea;
  const paperEfficiency = sheetArea > 0 ? Math.min(100, Math.max(0, (totalPrintedArea / sheetArea) * 100)) : 0;
  const paperWaste = Math.max(0, 100 - paperEfficiency);

  // Total Quantity determination
  const datasetDefaultQty = csvData?.rows?.length 
    ? (csvData.rows.length * (layout.quantityPerRecord || 1)) 
    : 1000;
  const totalRunQuantity = simQuantity > 0 ? simQuantity : datasetDefaultQty;

  // Single-Up (1 per sheet) vs Imposition (N-Up per sheet)
  const singleUpSheets = totalRunQuantity;
  const multiUpSheets = Math.ceil(totalRunQuantity / Math.max(1, activeGridCount));
  const sheetsSaved = Math.max(0, singleUpSheets - multiUpSheets);

  // Press Time calculations (in minutes)
  const singleUpMinutes = singleUpSheets / Math.max(1, pressSpeed);
  const multiUpMinutes = multiUpSheets / Math.max(1, pressSpeed);
  const minutesSaved = Math.max(0, singleUpMinutes - multiUpMinutes);

  // Financial Cost Breakdown
  // 1. Substrate
  const singleUpSubstrateCost = singleUpSheets * unitCost;
  const multiUpSubstrateCost = multiUpSheets * unitCost;
  const substrateSavings = Math.max(0, singleUpSubstrateCost - multiUpSubstrateCost);

  // 2. Ink / Click charge
  const singleUpInkCost = singleUpSheets * inkCost;
  const multiUpInkCost = multiUpSheets * inkCost;
  const inkSavings = Math.max(0, singleUpInkCost - multiUpInkCost);

  // 3. Labor / Press Operator Time Cost
  const singleUpLaborCost = (singleUpMinutes / 60) * laborRate;
  const multiUpLaborCost = (multiUpMinutes / 60) * laborRate;
  const laborSavings = Math.max(0, singleUpLaborCost - multiUpLaborCost);

  // Total Direct Production Cost
  const singleUpTotalCost = singleUpSubstrateCost + singleUpInkCost + singleUpLaborCost;
  const multiUpTotalCost = multiUpSubstrateCost + multiUpInkCost + multiUpLaborCost;
  const netSavingsAmount = Math.max(0, singleUpTotalCost - multiUpTotalCost);
  const savingsPercent = singleUpTotalCost > 0 ? ((netSavingsAmount / singleUpTotalCost) * 100).toFixed(1) : '0';

  // Per-unit cost
  const singleUpUnitCost = totalRunQuantity > 0 ? singleUpTotalCost / totalRunQuantity : 0;
  const multiUpUnitCost = totalRunQuantity > 0 ? multiUpTotalCost / totalRunQuantity : 0;

  // Environmental / ESG Impact calculations
  const currentSubstrate = availableSubstrates.find(p => p.id === layout.substrateType) || availableSubstrates[0];
  const gsm = layout.substrateGsm !== undefined ? layout.substrateGsm : currentSubstrate.weightGsm;
  const treesSaved = (sheetsSaved / 8333);
  const co2SavedKg = (sheetsSaved * 0.045);
  const waterSavedLiters = (sheetsSaved * 1.3);
  
  // Waste area in sq meters (assuming 72 points per inch = 28.346 pts per cm = 2834.6 pts per m)
  const ptsPerMeter = 2834.645;
  const wasteAreaPerSheetM2 = Math.max(0, sheetArea - totalPrintedArea) / (ptsPerMeter * ptsPerMeter);
  const totalWasteAreaM2 = wasteAreaPerSheetM2 * multiUpSheets;
  const totalWasteKg = totalWasteAreaM2 * (gsm / 1000);

  // Theoretical Auto-Nest Maximum Optimization Analysis
  const optimizationTips = useMemo(() => {
    const tips: {
      id: string;
      title: string;
      description: string;
      potentialAdditionalSavings: number;
      potentialStickersPerSheet: number;
      actionLabel: string;
      onApply: () => void;
      type: 'rotation' | 'margins' | 'size' | 'gaps';
    }[] = [];

    const availW = finalSheetWidth - (layout.marginX * 2);
    const availH = finalSheetHeight - (layout.marginY * 2);
    const bld = layout.bleed || 0;
    const rawW = template.width + (bld * 2);
    const rawH = template.height + (bld * 2);

    // 1. Rotation Comparison
    const colsUnrotated = Math.floor((availW + layout.gapX) / (rawW + layout.gapX));
    const rowsUnrotated = Math.floor((availH + layout.gapY) / (rawH + layout.gapY));
    const totalUnrotated = Math.max(0, colsUnrotated) * Math.max(0, rowsUnrotated);

    const colsRotated = Math.floor((availW + layout.gapX) / (rawH + layout.gapX));
    const rowsRotated = Math.floor((availH + layout.gapY) / (rawW + layout.gapY));
    const totalRotated = Math.max(0, colsRotated) * Math.max(0, rowsRotated);

    const currentN = activeGridCount;

    if (!layout.rotateStickers && totalRotated > currentN) {
      const extra = totalRotated - currentN;
      const optSheets = Math.ceil(totalRunQuantity / totalRotated);
      const addlSavings = Math.max(0, (multiUpSheets - optSheets) * (unitCost + inkCost));
      tips.push({
        id: 'rotate_90',
        title: `Rotate Stickers 90° (+${extra} items/sheet)`,
        description: `Rotating sticker orientation from portrait to landscape fits ${totalRotated} stickers instead of ${currentN} per sheet, saving an extra ${currSym}${addlSavings.toFixed(0)} and ${multiUpSheets - optSheets} sheets on this run.`,
        potentialAdditionalSavings: addlSavings,
        potentialStickersPerSheet: totalRotated,
        actionLabel: 'Apply 90° Rotation',
        type: 'rotation',
        onApply: () => {
          onUpdateLayout('rotateStickers', true);
          onUpdateLayout('cols', colsRotated);
          onUpdateLayout('rows', rowsRotated);
          toast.success('Layout Optimized', `Rotated stickers 90° to fit ${totalRotated} per sheet.`);
        }
      });
    } else if (layout.rotateStickers && totalUnrotated > currentN) {
      const extra = totalUnrotated - currentN;
      const optSheets = Math.ceil(totalRunQuantity / totalUnrotated);
      const addlSavings = Math.max(0, (multiUpSheets - optSheets) * (unitCost + inkCost));
      tips.push({
        id: 'unrotate',
        title: `Reset Orientation (+${extra} items/sheet)`,
        description: `Un-rotating orientation fits ${totalUnrotated} items per sheet instead of ${currentN}, saving an extra ${currSym}${addlSavings.toFixed(0)}.`,
        potentialAdditionalSavings: addlSavings,
        potentialStickersPerSheet: totalUnrotated,
        actionLabel: 'Reset Rotation',
        type: 'rotation',
        onApply: () => {
          onUpdateLayout('rotateStickers', false);
          onUpdateLayout('cols', colsUnrotated);
          onUpdateLayout('rows', rowsUnrotated);
          toast.success('Layout Optimized', `Set normal orientation to fit ${totalUnrotated} per sheet.`);
        }
      });
    }

    // 2. Margin Reduction Check (e.g., if reducing margin by 5-10 pts fits 1 more row or col)
    if (layout.marginX > 14 || layout.marginY > 14) {
      const tightMargin = 10;
      const tightAvailW = finalSheetWidth - (tightMargin * 2);
      const tightAvailH = finalSheetHeight - (tightMargin * 2);
      const tightW = layout.rotateStickers ? rawH : rawW;
      const tightH = layout.rotateStickers ? rawW : rawH;
      const tightCols = Math.floor((tightAvailW + layout.gapX) / (tightW + layout.gapX));
      const tightRows = Math.floor((tightAvailH + layout.gapY) / (tightH + layout.gapY));
      const tightTotal = Math.max(0, tightCols) * Math.max(0, tightRows);

      if (tightTotal > currentN) {
        const extra = tightTotal - currentN;
        const optSheets = Math.ceil(totalRunQuantity / tightTotal);
        const addlSavings = Math.max(0, (multiUpSheets - optSheets) * (unitCost + inkCost));
        tips.push({
          id: 'trim_margins',
          title: `Trim Outer Margins to 10pt (+${extra} items/sheet)`,
          description: `Reducing sheet margins from ${formatUnit(layout.marginX, appUnit)} to 10pt yields an extra column/row, increasing capacity to ${tightTotal} items per sheet.`,
          potentialAdditionalSavings: addlSavings,
          potentialStickersPerSheet: tightTotal,
          actionLabel: 'Apply 10pt Margins',
          type: 'margins',
          onApply: () => {
            onUpdateLayout('marginX', tightMargin);
            onUpdateLayout('marginY', tightMargin);
            onUpdateLayout('cols', tightCols);
            onUpdateLayout('rows', tightRows);
            toast.success('Margins Adjusted', `Set margins to 10pt, increasing capacity to ${tightTotal} items per sheet.`);
          }
        });
      }
    }

    // 3. A3 Upscale Check (if currently on A4)
    if (layout.pageSize === 'A4') {
      const a3W = 842;
      const a3H = 1190;
      const a3Cols = Math.floor((a3W - 20) / (rawW + layout.gapX));
      const a3Rows = Math.floor((a3H - 20) / (rawH + layout.gapY));
      const a3Total = Math.max(0, a3Cols) * Math.max(0, a3Rows);
      if (a3Total > currentN * 1.8) {
        tips.push({
          id: 'a3_upgrade',
          title: `A3 Commercial Press Sheet (${a3Total} items/sheet)`,
          description: `Doubling sheet size to A3 fits ${a3Total} stickers per sheet, reducing total press run passes from ${multiUpSheets} to ${Math.ceil(totalRunQuantity / a3Total)} passes (cuts print time by >50%).`,
          potentialAdditionalSavings: netSavingsAmount * 0.25,
          potentialStickersPerSheet: a3Total,
          actionLabel: 'Switch to A3 Sheet',
          type: 'size',
          onApply: () => {
            onUpdateLayout('pageSize', 'A3');
            onUpdateLayout('cols', a3Cols);
            onUpdateLayout('rows', a3Rows);
            toast.success('Switched to A3', `Updated target sheet to A3 with ${a3Total} stickers per sheet.`);
          }
        });
      }
    }

    return tips;
  }, [finalSheetWidth, finalSheetHeight, layout, template, activeGridCount, totalRunQuantity, multiUpSheets, unitCost, inkCost, netSavingsAmount, currSym, appUnit, onUpdateLayout, toast]);

  // Efficiency Rating category
  const efficiencyRating = useMemo(() => {
    if (paperEfficiency >= 75) return { label: 'Optimal Yield', color: 'bg-[#1473E6] text-white', textCol: 'text-emerald-300', icon: CheckCircle2 };
    if (paperEfficiency >= 55) return { label: 'Moderate Yield', color: 'bg-amber-500 text-white', textCol: 'text-amber-300', icon: Info };
    return { label: 'Suboptimal (High Trim Scrap)', color: 'bg-red-500 text-white', textCol: 'text-red-300', icon: AlertCircle };
  }, [paperEfficiency]);

  // Export / Print Job Ticket Summary
  const handleExportPdf = async () => {
    const element = document.getElementById('roi-audit-modal-body');
    if (!element || isExportingPdf) return;

    setIsExportingPdf(true);

    // Create a temporary container styled with white background and black text
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.top = '-9999px';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '1000px'; 
    tempContainer.style.background = '#ffffff';
    tempContainer.style.color = '#000000';
    tempContainer.style.padding = '32px';
    tempContainer.className = 'theme-light text-black';

    // Clone the printable ticket body
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.background = '#ffffff';
    clone.style.color = '#000000';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.classList.remove('text-zinc-200', 'bg-zinc-900', 'border-zinc-800');
    clone.classList.add('text-black', 'bg-white');

    // Create a style block to override dark styles to pristine high-contrast light styles
    const styleOverride = document.createElement('style');
    styleOverride.innerHTML = `
      * {
        color: #18181b !important; /* text-zinc-900 */
        border-color: #e4e4e7 !important; /* border-zinc-200 */
      }
      .bg-zinc-950, .bg-zinc-900, .bg-zinc-800 {
        background-color: #f4f4f5 !important; /* bg-zinc-100 */
        border: 1px solid #e4e4e7 !important;
      }
      .bg-zinc-900\\/50 {
        background-color: #ffffff !important;
      }
      .text-zinc-100, .text-zinc-200, .text-zinc-300, .text-zinc-400 {
        color: #18181b !important; /* text-zinc-900 */
      }
      .text-zinc-500, .text-zinc-600 {
        color: #71717a !important; /* text-zinc-500 */
      }
      .text-\\[\\#1473E6\\], .text-blue-700, .text-blue-600, .text-blue-500, .text-blue-400 {
        color: #1d4ed8 !important; /* blue-700 */
      }
      .bg-amber-500\\/10 {
        background-color: #fef3c7 !important; /* amber-50 */
        border-color: #fde68a !important; /* amber-200 */
      }
      .text-amber-500, .text-amber-400, .text-amber-300 {
        color: #b45309 !important; /* amber-700 */
      }
      .text-emerald-500, .text-emerald-400, .text-emerald-300 {
        color: #047857 !important; /* emerald-700 */
      }
      .text-orange-500, .text-orange-400, .text-orange-300 {
        color: #c2410c !important; /* orange-700 */
      }
      svg {
        color: #18181b !important;
        stroke: #18181b !important;
      }
    `;
    tempContainer.appendChild(styleOverride);
    tempContainer.appendChild(clone);
    document.body.appendChild(tempContainer);

    try {
      const dataUrl = await toPng(clone, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left'
        }
      });
      
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape for this modal
      const imgWidth = 297; // A4 width in mm (landscape)
      const pageHeight = 210; // A4 height in mm (landscape)
      const imgHeight = (clone.clientHeight * imgWidth) / clone.clientWidth;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('ROI_Audit_Report.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      document.body.removeChild(tempContainer);
      setIsExportingPdf(false);
    }
  };

  const handleExportCsvReport = () => {
    const csvContent = [
      ['Commercial Print Imposition & ROI Economics Audit'],
      ['Generated Date', new Date().toLocaleString()],
      ['Template Dimensions', `${formatUnit(template.width, appUnit)} x ${formatUnit(template.height, appUnit)} ${appUnit}`],
      ['Sheet Size', `${layout.pageSize} (${formatUnit(finalSheetWidth, appUnit)} x ${formatUnit(finalSheetHeight, appUnit)} ${appUnit})`],
      ['N-Up Imposition Grid', `${cols} Columns x ${rows} Rows (${activeGridCount} active slots)`],
      ['Total Batch Run Quantity', totalRunQuantity],
      [''],
      ['ECONOMIC COMPARISON', '1-Up Baseline', 'Imposition (Multi-Up)', 'Net Savings', 'Savings %'],
      ['Sheets Required', singleUpSheets, multiUpSheets, sheetsSaved, `${savingsPercent}%`],
      ['Substrate Material Cost', `${currSym}${singleUpSubstrateCost.toFixed(2)}`, `${currSym}${multiUpSubstrateCost.toFixed(2)}`, `${currSym}${substrateSavings.toFixed(2)}`, ''],
      ['Ink / Click Charges', `${currSym}${singleUpInkCost.toFixed(2)}`, `${currSym}${multiUpInkCost.toFixed(2)}`, `${currSym}${inkSavings.toFixed(2)}`, ''],
      ['Labor / Press Time Cost', `${currSym}${singleUpLaborCost.toFixed(2)}`, `${currSym}${multiUpLaborCost.toFixed(2)}`, `${currSym}${laborSavings.toFixed(2)}`, ''],
      ['TOTAL PRODUCTION COST', `${currSym}${singleUpTotalCost.toFixed(2)}`, `${currSym}${multiUpTotalCost.toFixed(2)}`, `${currSym}${netSavingsAmount.toFixed(2)}`, `${savingsPercent}%`],
      ['Cost per Finished Unit', `${currSym}${singleUpUnitCost.toFixed(3)}`, `${currSym}${multiUpUnitCost.toFixed(3)}`, `${currSym}${(singleUpUnitCost - multiUpUnitCost).toFixed(3)}`, ''],
      [''],
      ['ENVIRONMENTAL & SUSTAINABILITY IMPACT'],
      ['Sheet Area Coverage Efficiency', `${paperEfficiency.toFixed(1)}%`],
      ['Trim / Bleed Scrap Waste', `${paperWaste.toFixed(1)}% (${totalWasteKg.toFixed(2)} kg)`],
      ['Trees Biomass Conserved', treesSaved.toFixed(3)],
      ['Carbon CO2e Prevented', `${co2SavedKg.toFixed(1)} kg CO2e`],
      ['Industrial Water Conserved', `${waterSavedLiters.toFixed(1)} Liters`]
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `ROI_Economics_Audit_${layout.pageSize}_${totalRunQuantity}qty.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Audit Exported', 'Downloaded full pre-press ROI and ESG economics CSV.');
  };

  return (
    <>
      {props.compact ? (
        <div className="flex items-center gap-1 shrink-0">
          {/* Real-Time ROI Savings & Audit Button (Best feature: Shows live savings + opens full audit) */}
          <button
            id="btn-open-roi-audit"
            onClick={() => setShowAuditModal(true)}
            className="h-7 px-2.5 rounded-lg bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-600/70 text-emerald-300 flex items-center gap-1.5 transition-all text-xs font-semibold cursor-pointer shrink-0 shadow-xs group"
            title={`Real-Time ROI & Pre-Press Economics: ${currSym}${netSavingsAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })} (${savingsPercent}%) saved • ${paperEfficiency.toFixed(0)}% Paper Yield • Click to open complete Audit & Cost Breakdown`}
          >
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400 shrink-0 group-hover:scale-110 transition-transform" />
            <span className="font-mono font-bold text-emerald-300 text-[11px] whitespace-nowrap">
              {currSym}{netSavingsAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({savingsPercent}%)
            </span>
          </button>

          {/* Currency Switcher */}
          <select
            id="select-roi-currency"
            className="h-7 min-w-[76px] bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-[11px] font-mono font-bold px-2 rounded-md border border-zinc-700/80 outline-none cursor-pointer shrink-0 transition-colors"
            value={activeCode}
            onChange={(e) => switchCurrency(e.target.value, true)}
            title="Currency & Live Market Exchange Rates (Click to change)"
          >
            {supportedCurrencies.map(c => (
              <option key={c.code} value={c.code} className="bg-zinc-900 text-zinc-200 font-mono">
                {c.symbol} {c.code}
              </option>
            ))}
          </select>

          {/* AI Optimize Job Button */}
          <button
            onClick={() => setShowAIModal(true)}
            className="h-7 w-7 rounded-lg bg-violet-600/20 hover:bg-violet-600/40 border border-violet-500/40 text-violet-300 flex items-center justify-center transition-all cursor-pointer shrink-0"
            title="AI Job Layout Optimization: Auto-calculate optimal imposition to maximize yield and minimize cost"
          >
            <Bot className="w-3.5 h-3.5 text-violet-300" />
          </button>

          {/* Quick CSV Export Button */}
          <button
            onClick={handleExportCsvReport}
            className="h-7 w-7 rounded-lg bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 flex items-center justify-center transition-all cursor-pointer shrink-0"
            title="Export Full Pre-Press Cost Audit CSV Sheet"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        /* Real-Time ROI & Paper Savings Main Header Banner */
        <div className="flex flex-col bg-zinc-950 text-white rounded-xl shadow-md border border-zinc-800 transition-all w-full min-w-0 shrink-0">
        {/* Banner Content */}
        <div className="p-2 sm:p-2.5 px-3 flex flex-col">
          {/* Top Row: Header Badge & Quick Action Triggers */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-[#1473E6]/20 hover:bg-[#1473E6]/30 text-orange-400 text-xs font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border border-blue-600/30 flex items-center gap-1.5 shadow-xs cursor-pointer transition-colors whitespace-nowrap"
              >
                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <TrendingUp className="w-3.5 h-3.5 text-orange-400" />
                <span className="hidden md:inline">Real-Time ROI & Paper Savings</span>
                <span className="inline md:hidden">ROI Savings</span>
              </button>
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap ${efficiencyRating.color}`}>
                <efficiencyRating.icon className="w-3 h-3" />
                {efficiencyRating.label} ({paperEfficiency.toFixed(0)}%)
              </span>
              <span className="text-xs font-bold text-emerald-400 bg-emerald-950/80 px-2.5 py-1 rounded-md border border-emerald-800 flex items-center gap-1 whitespace-nowrap">
                {currSym}{netSavingsAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({savingsPercent}%) Saved
              </span>
              {disabledSlotCount > 0 && (
                <span className="bg-amber-500/20 text-amber-300 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-500/30 whitespace-nowrap">
                  {disabledSlotCount} Slot{disabledSlotCount > 1 ? 's' : ''} Skipped
                </span>
              )}
            </div>

            {/* Quick Currency & Full Audit Modal Button */}
            <div className="flex items-center flex-wrap gap-1.5 sm:gap-2 shrink-0">
              <div className="flex items-center bg-zinc-800 rounded-md border border-zinc-700 p-0.5">
                <select
                  id="select-roi-currency"
                  className="bg-zinc-900 hover:bg-zinc-800 text-zinc-200 text-xs font-bold px-2 py-1 rounded-md focus:ring-2 focus:ring-emerald-400 outline-none cursor-pointer transition-colors"
                  value={activeCode}
                  onChange={(e) => switchCurrency(e.target.value, true)}
                  title="Select Currency (Auto-converts all costs via live exchange rates)"
                >
                  {supportedCurrencies.map(c => (
                    <option key={c.code} value={c.code} className="bg-zinc-800">
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <div className="h-4 w-px bg-zinc-600 mx-1 hidden sm:block"></div>
                <div 
                  className="hidden sm:flex items-center gap-1 px-1.5 text-[10px] font-medium text-zinc-400"
                  title="Live Market Exchange Rate"
                >
                  <span className="truncate max-w-[100px]">{activeCode === 'USD' ? exchangeRateString('EUR') : exchangeRateString('USD')}</span>
                  <button 
                    onClick={refreshRates} 
                    disabled={isRefreshing}
                    className="p-0.5 hover:bg-zinc-700 hover:text-emerald-400 rounded-md transition-colors disabled:opacity-50"
                    title="Sync Latest Market Rates"
                  >
                    <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              <button
                onClick={() => setShowAIModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-md transition-all shadow-md shadow-violet-900/40 cursor-pointer group whitespace-nowrap"
                title="AI Job Optimization"
              >
                <Bot className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">AI Optimize</span>
              </button>

              <button
                id="btn-open-roi-audit"
                onClick={() => setShowAuditModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1473E6] hover:bg-[#1b85ff] text-white text-xs font-bold rounded-md transition-all shadow-md shadow-emerald-900/40 cursor-pointer group whitespace-nowrap"
              >
                <PieChart className="w-3.5 h-3.5" />
                <span className="inline">Audit</span>
                <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="pt-3.5 mt-2.5 border-t border-zinc-800/80 flex flex-col gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Primary High-Impact Metrics Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-center">
                {/* Left: Net Monetary Savings & Percentage Reduction */}
                <div className="xl:col-span-5 space-y-2">
                  <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                    Total Production Cost Reduction
                  </div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-3xl sm:text-4xl font-black text-emerald-400 tracking-tight font-mono">
                      {currSym}{netSavingsAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="text-sm sm:text-base font-bold text-emerald-300 bg-emerald-950/80 px-2.5 py-0.5 rounded-md border border-emerald-800">
                      {savingsPercent}% Saved
                    </span>
                  </div>
                  <div className="text-xs text-zinc-300 font-medium flex items-center gap-2 flex-wrap">
                    <span>Unit Cost: <strong className="text-white font-mono">{currSym}{multiUpUnitCost.toFixed(2)}</strong> / finished piece</span>
                    <span className="text-zinc-500">vs</span>
                    <span className="text-zinc-400 line-through font-mono">{currSym}{singleUpUnitCost.toFixed(2)} (1-Up)</span>
                  </div>
                </div>

                {/* Middle: Visual Sheet Surface Area Efficiency Meter */}
                <div className="xl:col-span-4 space-y-2 bg-zinc-950/60 p-3.5 rounded-md border border-zinc-800/80">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="text-emerald-300 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                      Active Print: <strong className="text-white ml-0.5">{paperEfficiency.toFixed(1)}%</strong>
                    </span>
                    <span className="text-zinc-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-zinc-600"></span>
                      Waste Trim: <strong className="text-zinc-300 ml-0.5">{paperWaste.toFixed(1)}%</strong>
                    </span>
                  </div>
                  {/* Stacked Gauge Bar */}
                  <div className="w-full bg-zinc-800 rounded-full h-3 p-0.5 border border-zinc-700 overflow-hidden flex">
                    <div 
                      className="bg-linear-to-r from-orange-500 to-teal-400 h-full rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${paperEfficiency}%` }}
                      title={`Active printed area: ${paperEfficiency.toFixed(1)}%`}
                    ></div>
                    <div 
                      className="bg-zinc-700 h-full transition-all duration-500"
                      style={{ width: `${paperWaste}%` }}
                      title={`Trim / Margin scrap waste: ${paperWaste.toFixed(1)}%`}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-zinc-400 pt-0.5">
                    <span>{activeGridCount} items / {layout.pageSize} sheet</span>
                    <span className="text-emerald-400 font-medium">
                      {optimizationTips.length > 0 ? `💡 ${optimizationTips.length} tip available` : '✓ Maximized layout'}
                    </span>
                  </div>
                </div>

                {/* Right: Instant Production Impact Metrics */}
                <div className="xl:col-span-3 flex flex-row sm:grid sm:grid-cols-2 gap-2 overflow-x-auto sm:overflow-visible pb-1 sm:pb-0">
                  <div className="bg-zinc-950/70 p-2.5 rounded-md border border-zinc-800 flex flex-col justify-between">
                    <div className="text-[10px] uppercase font-bold text-zinc-400 flex items-center gap-1">
                      <Layers className="w-3 h-3 text-emerald-400" />
                      Sheets Saved
                    </div>
                    <div className="text-lg font-black text-white font-mono mt-0.5">
                      {sheetsSaved.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-zinc-400">
                      {multiUpSheets} vs {singleUpSheets}
                    </div>
                  </div>
                  <div className="bg-zinc-950/70 p-2.5 rounded-md border border-zinc-800 flex flex-col justify-between">
                    <div className="text-[10px] uppercase font-bold text-zinc-400 flex items-center gap-1">
                      <Clock className="w-3 h-3 text-orange-400" />
                      Print Time
                    </div>
                    <div className="text-lg font-black text-white font-mono mt-0.5">
                      {minutesSaved >= 60 ? `${(minutesSaved / 60).toFixed(1)}h` : `${minutesSaved.toFixed(0)}m`}
                    </div>
                    <div className="text-[10px] text-zinc-400">
                      Machine saved
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Substrate & Cost Configuration Bar */}
              <div className="pt-4 mt-2 border-t border-zinc-800/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
            
            {/* Left: Material & Cost Configuration */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              
              <div className="flex items-center gap-2">
                <span className="text-zinc-400 font-medium text-[11px] uppercase tracking-wider">
                  Substrate:
                </span>
                <select
                  id="select-substrate-type"
                  value={layout.substrateType || 'standard_vinyl'}
                  onChange={(e) => {
                    const preset = availableSubstrates.find(p => p.id === e.target.value);
                    onUpdateLayout('substrateType', e.target.value);
                    if (preset) {
                      onUpdateLayout('substrateName', preset.name);
                      onUpdateLayout('substrateGsm', preset.weightGsm);
                      if (e.target.value !== 'custom') {
                        onUpdateLayout('costPerSheet', preset.defaultCost);
                      }
                    }
                  }}
                  className="bg-zinc-800 hover:bg-zinc-700/80 text-zinc-200 font-bold px-2.5 py-1.5 rounded-md border border-zinc-700 focus:ring-2 focus:ring-emerald-400 outline-none cursor-pointer text-xs transition-colors"
                >
                  {availableSubstrates.map(preset => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name} ({currSym}{preset.defaultCost})
                    </option>
                  ))}
                </select>
                
                <button
                  type="button"
                  onClick={() => setShowSubstrateModal(true)}
                  className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-orange-400 border border-zinc-700 rounded-md transition-colors cursor-pointer"
                  title="Customize Material Prices & GSM Density"
                >
                  <Sliders className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="hidden sm:block w-px h-5 bg-zinc-700"></div>

              <div className="flex items-center gap-3 bg-zinc-900/50 p-1.5 rounded-md border border-zinc-800">
                {/* Editable Sheet Cost */}
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">Sheet:</span>
                  <div className="flex items-center bg-zinc-800 px-2 py-0.5 rounded border border-zinc-700">
                    <span className="text-emerald-400 font-bold font-mono text-xs mr-1">{currSym}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      className="w-12 bg-transparent text-white font-mono font-bold text-xs outline-none"
                      value={unitCost}
                      onChange={(e) => onUpdateLayout('costPerSheet', Math.max(0, parseFloat(e.target.value) || 0))}
                    />
                  </div>
                </div>

                <div className="w-px h-3 bg-zinc-700"></div>

                {/* Ink Cost */}
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider">Ink:</span>
                  <div className="flex items-center bg-zinc-800 px-2 py-0.5 rounded border border-zinc-700">
                    <span className="text-orange-400 font-bold font-mono text-xs mr-1">{currSym}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      className="w-12 bg-transparent text-white font-mono font-bold text-xs outline-none"
                      value={inkCost}
                      onChange={(e) => onUpdateLayout('inkCostPerSheet', Math.max(0, parseFloat(e.target.value) || 0))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Quick Suggestions Alert or Action */}
            <div className="flex items-center">
              {optimizationTips.length > 0 ? (
                <button
                  onClick={() => {
                    optimizationTips[0].onApply();
                  }}
                  className="flex flex-1 md:flex-none items-center justify-center gap-2 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-md text-xs font-bold transition-all shadow-sm cursor-pointer group"
                  title={optimizationTips[0].description}
                >
                  <Zap className="w-3.5 h-3.5 text-amber-400 group-hover:scale-110 transition-transform" />
                  <span>Tip: {optimizationTips[0].title}</span>
                </button>
              ) : (
                <div className="flex flex-1 md:flex-none items-center justify-center gap-1.5 px-3 py-1.5 bg-[#1473E6]/10 text-emerald-400 border border-blue-600/20 rounded-md text-xs font-semibold shadow-sm">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Maximum Density</span>
                </div>
              )}
            </div>
          </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ========================================================================= */}
      {/* PRE-PRESS ECONOMIC & ENVIRONMENTAL AUDIT MODAL                            */}
      {/* ========================================================================= */}
      {showAuditModal && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4 bg-zinc-950/80 backdrop-blur-xs animate-in fade-in duration-200 select-none cursor-pointer"
          onClick={() => setShowAuditModal(false)}
        >
          <div 
            id="roi-audit-modal-body"
            className="bg-zinc-900 w-full max-w-4xl rounded-xl shadow-2xl border border-zinc-700/80 flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200 select-text cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 bg-zinc-900 text-white flex items-center justify-between border-b border-zinc-800 shrink-0">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="bg-[#1473E6]/20 text-emerald-300 text-xs font-black uppercase px-2.5 py-0.5 rounded-full border border-blue-600/30 flex items-center gap-1">
                    <BarChart3 className="w-3 h-3" /> Pre-Press Economics & ESG Audit
                  </span>
                  <span className="text-xs text-zinc-400">Job: {template.elements.length} Elements | {layout.pageSize} Sheet</span>
                </div>
                <h3 className="text-xl font-black text-white tracking-tight">
                  Comprehensive Imposition & ROI Breakdown
                </h3>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportCsvReport}
                  className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-bold rounded-md border border-zinc-700 transition-colors cursor-pointer"
                  title="Export complete CSV data sheet"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export CSV</span>
                </button>

                <button
                  onClick={handleExportPdf}
                  disabled={isExportingPdf}
                  className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-bold rounded-md border border-zinc-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export official production work-order sheet to PDF"
                >
                  {isExportingPdf ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileDown className="w-3.5 h-3.5" />
                  )}
                  <span>{isExportingPdf ? 'Exporting...' : 'Save PDF'}</span>
                </button>

                <button
                  onClick={() => setShowAuditModal(false)}
                  className="p-1.5 text-zinc-400 hover:text-white rounded-md hover:bg-zinc-800 transition-colors cursor-pointer ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center border-b border-zinc-800 px-6 bg-zinc-950 gap-2 shrink-0">
              <button
                onClick={() => setAuditTab('economics')}
                className={`flex items-center gap-2 py-3 px-3 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                  auditTab === 'economics'
                    ? 'border-orange-600 text-blue-600 bg-zinc-900 shadow-2xs'
                    : 'border-transparent text-zinc-400 hover:text-zinc-100'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                Cost Breakdown & What-If Simulator
              </button>

              <button
                onClick={() => setAuditTab('optimizer')}
                className={`flex items-center gap-2 py-3 px-3 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                  auditTab === 'optimizer'
                    ? 'border-orange-600 text-blue-600 bg-zinc-900 shadow-2xs'
                    : 'border-transparent text-zinc-400 hover:text-zinc-100'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                Pre-Press Optimizer Tips
                {optimizationTips.length > 0 && (
                  <span className="bg-amber-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-black">
                    {optimizationTips.length}
                  </span>
                )}
              </button>

              <button
                onClick={() => setAuditTab('esg')}
                className={`flex items-center gap-2 py-3 px-3 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                  auditTab === 'esg'
                    ? 'border-orange-600 text-blue-600 bg-zinc-900 shadow-2xs'
                    : 'border-transparent text-zinc-400 hover:text-zinc-100'
                }`}
              >
                <Leaf className="w-4 h-4" />
                Environmental ESG Impact
              </button>

              <button
                onClick={() => setAuditTab('quote')}
                className={`flex items-center gap-2 py-3 px-3 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                  auditTab === 'quote'
                    ? 'border-orange-600 text-blue-600 bg-zinc-900 shadow-2xs'
                    : 'border-transparent text-zinc-400 hover:text-zinc-100'
                }`}
              >
                <FileSpreadsheet className="w-4 h-4" />
                Client Quote & Margin Estimator
              </button>
            </div>

            {/* Modal Body Content */}
            <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-6">
              {/* ------------------------------------------------------------- */}
              {/* TAB 1: FINANCIAL BREAKDOWN & WHAT-IF SIMULATOR                */}
              {/* ------------------------------------------------------------- */}
              {auditTab === 'economics' && (
                <div className="flex flex-col gap-6">
                  {/* Interactive What-If Simulator Bar */}
                  <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-md flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <label className="text-xs font-bold text-zinc-300 flex items-center justify-between">
                        <span>Simulate Batch Quantity: <strong className="text-blue-600 font-mono text-sm">{totalRunQuantity.toLocaleString()} Units</strong></span>
                        {simQuantity > 0 && (
                          <button
                            onClick={() => setSimQuantity(0)}
                            className="text-[11px] text-zinc-400 hover:text-zinc-400 font-medium underline cursor-pointer"
                          >
                            Reset to CSV dataset ({datasetDefaultQty})
                          </button>
                        )}
                      </label>
                      <input
                        type="range"
                        min="100"
                        max="50000"
                        step="100"
                        value={totalRunQuantity}
                        onChange={(e) => setSimQuantity(parseInt(e.target.value, 10))}
                        className="w-full h-2 bg-zinc-700 rounded-md appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {[500, 1000, 5000, 20000].map(qty => (
                        <button
                          key={qty}
                          onClick={() => setSimQuantity(qty)}
                          className={`px-2.5 py-1 text-xs font-bold rounded-md transition-colors cursor-pointer ${
                            totalRunQuantity === qty 
                              ? 'bg-[#1473E6] text-white' 
                              : 'bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800'
                          }`}
                        >
                          {qty.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Comparative Side-by-Side Cost Matrix */}
                  <div className="overflow-x-auto border border-zinc-800 rounded-md shadow-2xs">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead className="bg-zinc-950 border-b border-zinc-800 text-zinc-400 font-bold uppercase tracking-wider">
                        <tr>
                          <th className="p-3">Cost Driver / Metric</th>
                          <th className="p-3 text-zinc-500">1-Up Baseline</th>
                          <th className="p-3 bg-blue-50 text-blue-900 border-l border-r border-blue-200">
                            Current Imposition ({activeGridCount}-Up)
                          </th>
                          <th className="p-3 text-emerald-700">Net Variance / Savings</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100 font-mono">
                        <tr>
                          <td className="p-3 font-sans font-semibold text-zinc-300">Sheets Required</td>
                          <td className="p-3 text-zinc-400">{singleUpSheets.toLocaleString()} sheets</td>
                          <td className="p-3 bg-blue-50/50 font-bold text-blue-800 border-l border-r border-blue-100">{multiUpSheets.toLocaleString()} sheets</td>
                          <td className="p-3 text-blue-600 font-bold">-{sheetsSaved.toLocaleString()} sheets (-{savingsPercent}%)</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-sans font-semibold text-zinc-300">Substrate Paper Cost</td>
                          <td className="p-3 text-zinc-400">{currSym}{singleUpSubstrateCost.toFixed(2)}</td>
                          <td className="p-3 bg-blue-50/50 text-blue-800 border-l border-r border-blue-100">{currSym}{multiUpSubstrateCost.toFixed(2)}</td>
                          <td className="p-3 text-blue-600 font-bold">-{currSym}{substrateSavings.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-sans font-semibold text-zinc-300">Digital Press Ink / Click Charges</td>
                          <td className="p-3 text-zinc-400">{currSym}{singleUpInkCost.toFixed(2)}</td>
                          <td className="p-3 bg-blue-50/50 text-blue-800 border-l border-r border-blue-100">{currSym}{multiUpInkCost.toFixed(2)}</td>
                          <td className="p-3 text-blue-600 font-bold">-{currSym}{inkSavings.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-sans font-semibold text-zinc-300">Press Operator Run Time</td>
                          <td className="p-3 text-zinc-400">{(singleUpMinutes / 60).toFixed(1)} hrs ({singleUpMinutes.toFixed(0)} min)</td>
                          <td className="p-3 bg-blue-50/50 text-blue-800 border-l border-r border-blue-100">{(multiUpMinutes / 60).toFixed(1)} hrs ({multiUpMinutes.toFixed(0)} min)</td>
                          <td className="p-3 text-blue-600 font-bold">-{(minutesSaved / 60).toFixed(1)} hrs saved</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-sans font-semibold text-zinc-300">Labor / Machine Overhead</td>
                          <td className="p-3 text-zinc-400">{currSym}{singleUpLaborCost.toFixed(2)}</td>
                          <td className="p-3 bg-blue-950/40 text-blue-300 border-l border-r border-blue-900/50">{currSym}{multiUpLaborCost.toFixed(2)}</td>
                          <td className="p-3 text-blue-400 font-bold">-{currSym}{laborSavings.toFixed(2)}</td>
                        </tr>
                        <tr className="bg-zinc-950 font-bold text-sm">
                          <td className="p-3 font-sans text-zinc-100">TOTAL DIRECT PRODUCTION COST</td>
                          <td className="p-3 text-zinc-300">{currSym}{singleUpTotalCost.toFixed(2)}</td>
                          <td className="p-3 bg-blue-950 text-blue-200 border-l border-r border-blue-800">{currSym}{multiUpTotalCost.toFixed(2)}</td>
                          <td className="p-3 text-emerald-400 font-black">-{currSym}{netSavingsAmount.toFixed(2)} ({savingsPercent}%)</td>
                        </tr>
                        <tr className="bg-emerald-950/40 font-bold text-sm">
                          <td className="p-3 font-sans text-emerald-300">COST PER FINISHED STICKER / UNIT</td>
                          <td className="p-3 text-zinc-500 line-through">{currSym}{singleUpUnitCost.toFixed(3)}</td>
                          <td className="p-3 bg-emerald-950 text-emerald-300 border-l border-r border-emerald-800">{currSym}{multiUpUnitCost.toFixed(3)}</td>
                          <td className="p-3 text-emerald-400 font-black">-{currSym}{(singleUpUnitCost - multiUpUnitCost).toFixed(3)} / unit</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Visual Cost Allocation Proportions */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-md flex flex-col justify-between">
                      <span className="text-xs font-bold text-zinc-500 uppercase">Substrate Share</span>
                      <div className="text-2xl font-black text-zinc-200 font-mono mt-1">
                        {multiUpTotalCost > 0 ? `${((multiUpSubstrateCost / multiUpTotalCost) * 100).toFixed(0)}%` : '0%'}
                      </div>
                      <span className="text-xs text-zinc-500 mt-1">{currSym}{multiUpSubstrateCost.toFixed(2)} of total budget</span>
                    </div>

                    <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-md flex flex-col justify-between">
                      <span className="text-xs font-bold text-zinc-500 uppercase">Ink / Toner Share</span>
                      <div className="text-2xl font-black text-zinc-200 font-mono mt-1">
                        {multiUpTotalCost > 0 ? `${((multiUpInkCost / multiUpTotalCost) * 100).toFixed(0)}%` : '0%'}
                      </div>
                      <span className="text-xs text-zinc-500 mt-1">{currSym}{multiUpInkCost.toFixed(2)} of total budget</span>
                    </div>

                    <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-md flex flex-col justify-between">
                      <span className="text-xs font-bold text-zinc-500 uppercase">Labor & Press Overhead</span>
                      <div className="text-2xl font-black text-zinc-200 font-mono mt-1">
                        {multiUpTotalCost > 0 ? `${((multiUpLaborCost / multiUpTotalCost) * 100).toFixed(0)}%` : '0%'}
                      </div>
                      <span className="text-xs text-zinc-500 mt-1">{currSym}{multiUpLaborCost.toFixed(2)} of total budget</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* TAB 2: PRE-PRESS OPTIMIZER SUGGESTIONS                        */}
              {/* ------------------------------------------------------------- */}
              {auditTab === 'optimizer' && (
                <div className="flex flex-col gap-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-sm block mb-0.5">Automated Imposition Density Analysis</strong>
                      Our algorithmic geometry solver continuously tests alternate 90° rotations, reduced margins, gutter alignments, and sheet scale sizes to minimize white trim scrap and maximize throughput.
                    </div>
                  </div>

                  {optimizationTips.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      {optimizationTips.map(tip => (
                        <div 
                          key={tip.id}
                          className="p-5 bg-zinc-900 border border-amber-200 hover:border-amber-400 rounded-md shadow-xs transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                        >
                          <div className="space-y-1.5 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="bg-amber-100 text-amber-900 text-xs font-black px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                <Zap className="w-3 h-3 text-amber-600" />
                                {tip.title}
                              </span>
                              {tip.potentialAdditionalSavings > 0 && (
                                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                                  +Save {currSym}{tip.potentialAdditionalSavings.toFixed(0)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-zinc-400 leading-relaxed">
                              {tip.description}
                            </p>
                          </div>

                          <button
                            onClick={() => {
                              tip.onApply();
                            }}
                            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-xs font-bold transition-all shadow-md shadow-amber-500/20 shrink-0 cursor-pointer flex items-center gap-1.5"
                          >
                            <Zap className="w-3.5 h-3.5" />
                            {tip.actionLabel}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-12 text-center text-zinc-400 flex flex-col items-center justify-center gap-2">
                      <CheckCircle2 className="w-10 h-10 text-blue-500" />
                      <span className="font-bold text-base text-zinc-200">Perfect Imposition Density</span>
                      <p className="text-xs text-zinc-500 max-w-md">
                        Your current layout ({activeGridCount} stickers per {layout.pageSize} sheet) achieves the mathematical maximum yield possible for this template aspect ratio and sheet size.
                      </p>
                    </div>
                  )}

                  {/* Auto-Nest and Center Shortcut Actions */}
                  <div className="p-5 bg-zinc-950 border border-zinc-800 rounded-md flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <span className="font-bold text-xs text-zinc-200 block">Fast Algorithmic Solvers</span>
                      <span className="text-xs text-zinc-500">Run global recalculation across margins, gutters, and rotations.</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={onAutoCalculate}
                        className="px-3.5 py-2 bg-[#1473E6] hover:bg-[#1b85ff] text-white text-xs font-bold rounded-md transition-all shadow-2xs cursor-pointer flex items-center gap-1.5"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Run Auto-Nest Optimizer
                      </button>

                      <button
                        onClick={onCenterOnPage}
                        className="px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 text-xs font-bold rounded-md transition-all cursor-pointer"
                      >
                        Auto-Center Margins
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* TAB 3: ENVIRONMENTAL & ESG SUSTAINABILITY                    */}
              {/* ------------------------------------------------------------- */}
              {auditTab === 'esg' && (
                <div className="flex flex-col gap-6">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-950 flex items-start gap-3">
                    <Leaf className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-sm block mb-0.5">Corporate Sustainability & ESG Reporting</strong>
                      By imposing multiple variable items onto standardized master sheets instead of printing separate carrier sheets, you drastically reduce commercial pulp harvesting, energy consumption, and landfill scrap.
                    </div>
                  </div>

                  {/* Big Visual Eco Badges */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-5 bg-emerald-950 text-white rounded-md border border-emerald-800 flex flex-col justify-between shadow-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-300 uppercase">Biomass Conserved</span>
                        <Leaf className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="text-3xl font-black text-emerald-400 font-mono mt-3">
                        {treesSaved.toFixed(3)}
                      </div>
                      <span className="text-[11px] text-emerald-200 mt-1">Trees preserved from paper pulp</span>
                    </div>

                    <div className="p-5 bg-teal-950 text-white rounded-md border border-teal-800 flex flex-col justify-between shadow-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-teal-300 uppercase">CO2 Footprint Avoided</span>
                        <TrendingUp className="w-4 h-4 text-teal-400" />
                      </div>
                      <div className="text-3xl font-black text-teal-400 font-mono mt-3">
                        {co2SavedKg.toFixed(1)} <span className="text-sm font-normal text-teal-200">kg</span>
                      </div>
                      <span className="text-[11px] text-teal-200 mt-1">Greenhouse gas emission reduction</span>
                    </div>

                    <div className="p-5 bg-cyan-950 text-white rounded-md border border-cyan-800 flex flex-col justify-between shadow-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-cyan-300 uppercase">Water Conserved</span>
                        <PieChart className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className="text-3xl font-black text-cyan-400 font-mono mt-3">
                        {waterSavedLiters.toFixed(0)} <span className="text-sm font-normal text-cyan-200">L</span>
                      </div>
                      <span className="text-[11px] text-cyan-200 mt-1">Industrial pulp manufacturing water</span>
                    </div>

                    <div className="p-5 bg-zinc-900 text-white rounded-md border border-zinc-800 flex flex-col justify-between shadow-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-zinc-400 uppercase">Waste Scrap Area</span>
                        <Scissors className="w-4 h-4 text-zinc-400" />
                      </div>
                      <div className="text-3xl font-black text-zinc-200 font-mono mt-3">
                        {totalWasteAreaM2.toFixed(1)} <span className="text-sm font-normal text-zinc-400">m²</span>
                      </div>
                      <span className="text-[11px] text-zinc-400 mt-1">{totalWasteKg.toFixed(2)} kg solid scrap trimmed</span>
                    </div>
                  </div>

                  {/* Sustainability Metrics Table */}
                  <div className="p-5 bg-zinc-950 border border-zinc-800 rounded-md space-y-3">
                    <h4 className="text-xs font-bold text-zinc-200 uppercase tracking-wider">
                      ESG Calculation Reference Standards
                    </h4>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      Metrics calculated according to Environmental Paper Network (EPN) methodology based on {currentSubstrate.name} ({currentSubstrate.weightGsm} GSM) substrate density across {multiUpSheets.toLocaleString()} master sheets.
                    </p>
                  </div>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* TAB 4: CLIENT QUOTE & MARGIN PRICING ESTIMATOR                */}
              {/* ------------------------------------------------------------- */}
              {auditTab === 'quote' && (
                <div className="flex flex-col gap-6">
                  {/* Margin Slider */}
                  <div className="p-5 bg-zinc-950 border border-zinc-800 rounded-md space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-bold text-sm text-zinc-100 block">Commercial Print Markup & Selling Price</span>
                        <span className="text-xs text-zinc-500">Calculate client quote recommendations based on your desired gross margin.</span>
                      </div>
                      <span className="text-xl font-black text-blue-400 font-mono bg-blue-950 border border-blue-800 px-3 py-1 rounded-md">
                        {targetMarginPercent}% Margin
                      </span>
                    </div>

                    <input
                      type="range"
                      min="10"
                      max="85"
                      step="5"
                      value={targetMarginPercent}
                      onChange={(e) => setTargetMarginPercent(parseInt(e.target.value, 10))}
                      className="w-full h-2 bg-zinc-700 rounded-md appearance-none cursor-pointer accent-blue-600"
                    />

                    <div className="flex items-center justify-between text-xs text-zinc-500">
                      <span>10% (High Volume Wholesale)</span>
                      <span>50% (Standard Retail)</span>
                      <span>85% (Boutique Custom / Rapid Turnaround)</span>
                    </div>
                  </div>

                  {/* Pricing Breakdown Cards */}
                  {(() => {
                    const marginMultiplier = 1 / (1 - (targetMarginPercent / 100));
                    const recommendedQuoteTotal = multiUpTotalCost * marginMultiplier;
                    const recommendedQuotePerUnit = totalRunQuantity > 0 ? recommendedQuoteTotal / totalRunQuantity : 0;
                    const grossProfit = recommendedQuoteTotal - multiUpTotalCost;

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-5 bg-zinc-900 text-white rounded-md border border-zinc-800 flex flex-col justify-between">
                          <span className="text-xs font-bold text-zinc-400 uppercase">Direct Production Cost</span>
                          <div className="text-3xl font-black text-white font-mono mt-2">
                            {currSym}{multiUpTotalCost.toFixed(2)}
                          </div>
                          <span className="text-xs text-zinc-400 mt-1">{currSym}{multiUpUnitCost.toFixed(3)} per finished item</span>
                        </div>

                        <div className="p-5 bg-emerald-950 text-white rounded-md border border-emerald-800 flex flex-col justify-between">
                          <span className="text-xs font-bold text-emerald-300 uppercase">Gross Profit Earned</span>
                          <div className="text-3xl font-black text-emerald-400 font-mono mt-2">
                            {currSym}{grossProfit.toFixed(2)}
                          </div>
                          <span className="text-xs text-emerald-200 mt-1">{targetMarginPercent}% profit margin on job</span>
                        </div>

                        <div className="p-5 bg-blue-950 text-white rounded-md border border-blue-800 flex flex-col justify-between">
                          <span className="text-xs font-bold text-orange-300 uppercase">Recommended Client Quote</span>
                          <div className="text-3xl font-black text-orange-400 font-mono mt-2">
                            {currSym}{recommendedQuoteTotal.toFixed(2)}
                          </div>
                          <span className="text-xs text-blue-200 mt-1 font-bold">{currSym}{recommendedQuotePerUnit.toFixed(2)} / finished sticker</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Printable Summary Sheet Preview */}
                  <div className="p-6 bg-zinc-900 border border-zinc-700 rounded-md shadow-xs space-y-4">
                    <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
                      <div>
                        <h4 className="font-black text-base text-zinc-100">Official Production Estimate & Work Order</h4>
                        <span className="text-xs text-zinc-500">VDP Studio Imposition Engine | Job #{Math.floor(Date.now() / 1000).toString().slice(-6)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-bold text-zinc-300 block">Date: {new Date().toLocaleDateString()}</span>
                        <span className="text-xs text-zinc-500">Substrate: {currentSubstrate.name}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-zinc-400 block font-medium">Finished Size</span>
                        <span className="font-bold text-zinc-200">{formatUnit(template.width, appUnit)} x {formatUnit(template.height, appUnit)} {appUnit}</span>
                      </div>
                      <div>
                        <span className="text-zinc-400 block font-medium">Sheet Format</span>
                        <span className="font-bold text-zinc-200">{layout.pageSize} ({cols}x{rows} Grid)</span>
                      </div>
                      <div>
                        <span className="text-zinc-400 block font-medium">Total Quantity</span>
                        <span className="font-bold text-zinc-200">{totalRunQuantity.toLocaleString()} labels</span>
                      </div>
                      <div>
                        <span className="text-zinc-400 block font-medium">Sheets to Print</span>
                        <span className="font-bold text-zinc-200">{multiUpSheets.toLocaleString()} master sheets</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer Actions */}
            <div className="p-4 bg-zinc-950 border-t border-zinc-800 flex items-center justify-between shrink-0">
              <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                <Info className="w-4 h-4 text-zinc-400" />
                <span>All estimates update dynamically with template dimensions and sheet nesting adjustments.</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAuditModal(false)}
                  className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-md text-xs font-bold transition-colors cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Interactive Substrate Customizer & Pricing Modal */}
      <SubstrateCustomizerModal
        isOpen={showSubstrateModal}
        onClose={() => setShowSubstrateModal(false)}
        currentSubstrateId={layout.substrateType || 'standard_vinyl'}
        currentCostPerSheet={unitCost}
        currencySymbol={currSym}
        onSelectSubstrate={(preset, cost) => {
          onUpdateLayout('substrateType', preset.id);
          onUpdateLayout('substrateName', preset.name);
          onUpdateLayout('substrateGsm', preset.weightGsm);
          if (cost !== undefined) {
            onUpdateLayout('costPerSheet', cost);
          }
        }}
      />

      <AICalculatorModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        layout={layout}
        template={template}
        activeGridCount={activeGridCount}
        rows={rows}
        cols={cols}
        paperEfficiency={paperEfficiency}
        paperWaste={paperWaste}
        totalRunQuantity={totalRunQuantity}
        multiUpSheets={multiUpSheets}
        currency={currSym}
        unitCost={unitCost}
        inkCost={inkCost}
        laborRate={laborRate}
        totalCost={multiUpTotalCost}
        unitCostPerSticker={multiUpUnitCost}
      />
    </>
  );
};

